<template>
  <div class="search-page">
    <search-form />

    <div
      v-if="showResults"
      class="container is-widescreen pt-4 pb-6 result-panel borderBox"
    >
      <section class="pt-0">
        <div class="columns">
          <!-- Filter Sidebar -->
          <div class="column is-3 filter-sidebar">

             <hotel-map-modal class="mb-10" :data="visibleHotels" :search-location="searchMeta.searchedLocation" :price-range="{min: priceRange[0], max: priceRange[1]}" :disabled="!resultsLoaded" />

            <form class="pr-4">
              <!-- Price Range -->
              <div class="filter-section">
                <h3
                  class="title is-5 mb-4"
                  :class="{ 'has-text-grey': !resultsLoaded }"
                >
                  Price
                </h3>

                <b-slider
                  v-if="maxPrice > 0"
                  v-model="priceRange"
                  :min="minPrice"
                  :max="maxPrice"
                  :step="1"
                  class="price-slider mb-4"
                  tooltip-always
                  lazy
                />

                <div class="price-inputs" v-if="maxPrice > 0">
                  <div class="field has-addons">
                    <p class="control">
                      <a class="button is-static narrow">$</a>
                    </p>
                    <p class="control mr-2">
                      <input
                        v-model.lazy="filters.price.range[0]"
                        class="input"
                        type=""
                        placeholder="Min"
                        :disabled="!resultsLoaded"
                      />
                    </p>
                  </div>

                  <div class="field has-addons">
                    <p class="control">
                      <a class="button is-static narrow">$</a>
                    </p>
                    <p class="control">
                      <input
                        v-model.lazy="filters.price.range[1]"
                        class="input"
                        type=""
                        placeholder="Max"
                        :disabled="!resultsLoaded"
                      />
                    </p>
                  </div>
                </div>

                <div v-else class="price-inputs">
                  <div class="field has-addons" style="width:49%;opacity: 0.3;">
                    <b-skeleton
                      width="100%"
                      height="40px"
                      :animated="false"
                    ></b-skeleton>
                  </div>

                  <div class="field has-addons" style="width:49%;opacity: 0.3;">
                    <b-skeleton
                      width="100%"
                      height="40px"
                      :animated="false"
                    ></b-skeleton>
                  </div>
                </div>
              </div>

              <!-- Radio Filters -->

              <!-- Company Preferred Hotels Filter -->
              <div v-if="hasPreferredHotels" class="filter-section">
                <b-collapse :open="true" :animation="'slide'">
                  <template #trigger>
                    <div
                      class="collapse-trigger"
                      :class="{ 'has-text-grey': !resultsLoaded }"
                    >
                      <h3 class="title is-5">Company Preferred Hotels</h3>
                      <b-icon icon="chevron-down" size="is-small" />
                    </div>
                  </template>

                  <div class="filter-options has-max-height-scroll">
                    <b-field
                      v-for="option in filters.companyPreferred.options"
                      :key="option.value"
                      class="mb-1"
                    >
                      <b-radio
                        v-model="filters.companyPreferred.selected"
                        :native-value="option.value"
                        :disabled="!resultsLoaded"
                      >
                        {{ option.label }}
                      </b-radio>
                    </b-field>
                  </div>
                </b-collapse>
              </div>
              <div
                v-for="section in radioFilters"
                :key="section.id"
                class="filter-section"
              >
                <b-collapse :open="true" :animation="'slide'">
                  <template #trigger>
                    <div
                      class="collapse-trigger"
                      :class="{ 'has-text-grey': !resultsLoaded }"
                    >
                      <h3 class="title is-5">{{ section.name }}</h3>
                      <b-icon icon="chevron-down" size="is-small" />
                    </div>
                  </template>

                  <div class="filter-options has-max-height-scroll">
                    <b-field
                      v-for="option in section.options"
                      :key="option.value"
                      class="mb-1"
                    >
                      <b-radio
                        v-model="section.selected"
                        :native-value="option.value"
                        :disabled="!resultsLoaded"
                      >
                        <div class="option-label">
                          <b-icon
                            pack="fas"
                            v-if="option.icon"
                            :icon="option.icon"
                            size="is-small"
                            class="mr-2"
                          />
                          {{ option.label }}
                        </div>
                      </b-radio>
                    </b-field>
                  </div>
                </b-collapse>
              </div>

              <!-- Checkbox Filters -->
              <div
                v-for="section in checkboxFilters"
                :key="section.id"
                class="filter-section"
              >
                <b-collapse :open="true" :animation="'slide'">
                  <template #trigger>
                    <div
                      class="collapse-trigger"
                      :class="{ 'has-text-grey': !resultsLoaded }"
                    >
                      <h3 class="title is-5">{{ section.name }}</h3>
                      <b-icon icon="chevron-down" size="is-small" />
                    </div>
                  </template>

                  <div class="filter-options has-max-height-scroll">
                    <template v-if="section.highlightedOptions">
                      <b-field
                        v-for="option in section.highlightedOptions"
                        :key="option.value"
                        class="mb-1 border-b-2 border-grey-200"
                      >
                        <b-checkbox
                          v-model="section.selected"
                          :native-value="option.value"
                          :disabled="!resultsLoaded"
                        >
                          <div class="option-label">
                            <b-icon
                              pack="fas"
                              v-if="option.icon"
                              :icon="option.icon"
                              size="is-small"
                              class="mr-2"
                            />
                            {{ option.label }}

                            <i
                              class="fa fa-solid fa-star ml-1 has-text-success fa-sm"
                            />
                          </div>
                        </b-checkbox>
                      </b-field>
                    </template>

                    <b-field
                      v-for="option in section.options"
                      :key="option.value"
                      class="mb-1"
                    >
                      <b-checkbox
                        v-model="section.selected"
                        :native-value="option.value"
                        :disabled="!resultsLoaded"
                      >
                        <div class="option-label">
                          <b-icon
                            pack="fas"
                            v-if="option.icon"
                            :icon="option.icon"
                            size="is-small"
                            class="mr-2"
                          />
                          {{ option.label }}
                        </div>
                      </b-checkbox>
                    </b-field>
                  </div>
                </b-collapse>
              </div>
            </form>
          </div>

          <!-- Results Column -->
          <div class="column">
            <div class="level is-mobile mb-4">
              <div class="level-right">
                <div class="level-item">
                  <span class="mr-2">Sort</span>
                  <b-select v-model="sortOrder">
                    <option
                      v-for="option in sortOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.name }}
                    </option>
                  </b-select>
                </div>

                <div class="level-item">
                  <button
                    class="button is-light is-hidden-desktop"
                    @click="isFilterModalActive = true"
                  >
                    <b-icon icon="tune" />
                    <span>Filters</span>
                  </button>
                </div>
              </div>
            </div>

            <ResultsList v-if="showResults" :cacheKey="cacheKey" />
          </div>
        </div>
      </section>
    </div>

    <!-- Mobile Filter Modal -->
    <b-modal v-model="isFilterModalActive" :width="320" scroll="keep">
      <div class="modal-card" style="width: auto">
        <header class="modal-card-head">
          <p class="modal-card-title">Filters</p>
          <button
            class="delete"
            type="button"
            @click="isFilterModalActive = false"
          />
        </header>
        <section class="modal-card-body">
          <!-- Mobile filter form - same content as sidebar -->
          <form class="mobile-filters">

             <hotel-map-modal class="mb-10" :data="visibleHotels" :search-location="searchMeta.searchedLocation" :price-range="{min: priceRange[0], max: priceRange[1]}" :disabled="!resultsLoaded" />

            <!-- Price Range -->
            <div class="filter-section">
              <h3
                class="title is-5 mb-4"
                :class="{ 'has-text-grey': !resultsLoaded }"
              >
                Price
              </h3>

              <b-slider
                v-if="maxPrice > 0"
                v-model="priceRange"
                :min="minPrice"
                :max="maxPrice"
                :step="1"
                class="price-slider mb-4"
                tooltip-always
                lazy
              />

              <div class="price-inputs">
                <div class="field has-addons">
                  <p class="control">
                    <a class="button is-static narrow">$</a>
                  </p>
                  <p class="control">
                    <input
                      v-model.lazy="filters.price.range[0]"
                      class="input"
                      type="number"
                      placeholder="Min"
                      :disabled="!resultsLoaded"
                    />
                  </p>
                </div>

                <div class="field has-addons">
                  <p class="control">
                    <a class="button is-static narrow">$</a>
                  </p>
                  <p class="control">
                    <input
                      v-model.lazy="filters.price.range[1]"
                      class="input"
                      type="number"
                      placeholder="Max"
                      :disabled="!resultsLoaded"
                    />
                  </p>
                </div>
              </div>
            </div>

            <!-- Radio Filters -->

            <!-- Company Preferred Hotels Filter (Mobile) -->
            <div v-if="hasPreferredHotels" class="filter-section">
              <b-collapse :open="true" :animation="'slide'">
                <template #trigger>
                  <div
                    class="collapse-trigger"
                    :class="{ 'has-text-grey': !resultsLoaded }"
                  >
                    <h3 class="title is-5">Company Preferred Hotels</h3>
                    <b-icon icon="chevron-down" size="is-small" />
                  </div>
                </template>

                <div class="filter-options has-max-height-scroll">
                  <b-field
                    v-for="option in filters.companyPreferred.options"
                    :key="option.value"
                    class="mb-1"
                  >
                    <b-radio
                      v-model="filters.companyPreferred.selected"
                      :native-value="option.value"
                      :disabled="!resultsLoaded"
                    >
                      {{ option.label }}
                    </b-radio>
                  </b-field>
                </div>
              </b-collapse>
            </div>
            <div
              v-for="section in radioFilters"
              :key="section.id"
              class="filter-section"
            >
              <b-collapse :open="true" :animation="'slide'">
                <template #trigger>
                  <div
                    class="collapse-trigger"
                    :class="{ 'has-text-grey': !resultsLoaded }"
                  >
                    <h3 class="title is-5">{{ section.name }}</h3>
                    <b-icon icon="chevron-down" size="is-small" />
                  </div>
                </template>

                <div class="filter-options has-max-height-scroll">
                  <b-field
                    v-for="option in section.options"
                    :key="option.value"
                    class="mb-1"
                  >
                    <b-radio
                      v-model="section.selected"
                      :native-value="option.value"
                      :disabled="!resultsLoaded"
                    >
                      <div class="option-label">
                        <b-icon
                          pack="fas"
                          v-if="option.icon"
                          :icon="option.icon"
                          size="is-small"
                          class="mr-2"
                        />
                        {{ option.label }}
                      </div>
                    </b-radio>
                  </b-field>
                </div>
              </b-collapse>
            </div>

            <!-- Checkbox Filters -->
            <div
              v-for="section in checkboxFilters"
              :key="section.id"
              class="filter-section"
            >
              <b-collapse :open="true" :animation="'slide'">
                <template #trigger>
                  <div
                    class="collapse-trigger"
                    :class="{ 'has-text-grey': !resultsLoaded }"
                  >
                    <h3 class="title is-5">{{ section.name }}</h3>
                    <b-icon icon="chevron-down" size="is-small" />
                  </div>
                </template>

                <div class="filter-options has-max-height-scroll">
                  <template v-if="section.highlightedOptions">
                    <b-field
                      v-for="option in section.highlightedOptions"
                      :key="option.value"
                      class="mb-1 border-b-2 border-grey-200"
                    >
                      <b-checkbox
                        v-model="section.selected"
                        :native-value="option.value"
                        :disabled="!resultsLoaded"
                      >
                        <div class="option-label">
                          <b-icon
                            pack="fas"
                            v-if="option.icon"
                            :icon="option.icon"
                            size="is-small"
                            class="mr-2"
                          />
                          {{ option.label }}

                          <i
                            class="fa fa-solid fa-star ml-1 has-text-success fa-sm"
                          />
                        </div>
                      </b-checkbox>
                    </b-field>
                  </template>

                  <b-field
                    v-for="option in section.options"
                    :key="option.value"
                    class="mb-1"
                  >
                    <b-checkbox
                      v-model="section.selected"
                      :native-value="option.value"
                      :disabled="!resultsLoaded"
                    >
                      <div class="option-label">
                        <b-icon
                          pack="fas"
                          v-if="option.icon"
                          :icon="option.icon"
                          size="is-small"
                          class="mr-2"
                        />
                        {{ option.label }}
                      </div>
                    </b-checkbox>
                  </b-field>
                </div>
              </b-collapse>
            </div>
          </form>
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex"
import ResultsList from "@/components/BookTravel/Hotel/ResultsList.vue"
import SearchForm from "@/components/BookTravel/Hotel/SearchForm.vue"
import PreferredVendorTag from "@/components/Tags/PreferredVendorTag.vue"
import HotelMapModal from '@/components/GMap/HotelMapModal.vue';

export default {
  components: {
    ResultsList,
    SearchForm,
    HotelMapModal
  },

  props: {},

  data() {
    return {
      isFilterModalActive: false,
      sortOptions: [
        { name: "Recommended", value: "Recommended" },
        { name: "Price (Low to High)", value: "Price Asc" },
        { name: "Price (High to Low)", value: "Price Desc" },
        { name: "Alphabetical", value: "Alphabetical" },
        { name: "Proximity", value: "Proximity" },
      ],
      priceRange: [0, 1000], // Initial values, should be updated from store
    }
  },

  computed: {
    ...mapState({
      minPrice: (state) => state.hotelSearch.minPrice,
      maxPrice: (state) => state.hotelSearch.maxPrice,
      filters: (state) => state.hotelSearch.filters,
      resultsLoaded: (state) => state.hotelSearch.searchResults.length > 0,
      searchDates: (state) => state.hotelSearch.searchDates,
      recentSearches: (state) => state.hotelSearch.recentSearches,
      searchResults: (state) => state.hotelSearch.searchResults,
      searchMeta: (state) => state.hotelSearch.searchMeta,

      visibleHotels: (state) => state.hotelSearch.searchResults.filter((hotel) => hotel.isVisible),
    }),

    hasPreferredHotels() {
      return this.searchResults?.some((hotel) => hotel.isPreferred)
    },

    showResults: {
      get() {
        return this.$store.state.hotelSearch.showResults
      },
      set(value) {
        this.$store.commit("hotelSearch/SET_SHOW_RESULTS", value)
      },
    },

    cacheKey() {
      return this.$route.query.key
    },

    checkboxFilters() {
      return [this.filters.brands, this.filters.chains, this.filters.amenities]
    },

    radioFilters() {
      return [this.filters.policy]
    },

    sortOrder: {
      get() {
        return this.$store.state.hotelSearch.sortOrder
      },
      set(value) {
        this.$store.dispatch("hotelSearch/updateSortOrder", value)
      },
    },
  },

  watch: {
    priceRange(newValue) {
      this.$store.commit("hotelSearch/SET_PRICE_FILTER_RANGE", newValue)
    },
  },

  mounted() {

    // Log the event
    this.$app.logEvent({
      event_type: 'Search Started',
      event_properties: {
        item_type: 'hotel'
      }
    });

    // Don't show ResultsList component until form is submitted unless there is a cache key in the url
    if (this.$route.query.key) {
      this.$store.dispatch("hotelSearch/getData", {
        cacheKey: this.$route.query.key,
      })
      this.showResults = true
    } else {
      this.showResults = false
    }
  },
}
</script>

<style lang="scss">
.has-max-height-scroll {
  max-height: 24rem;
  overflow-y: auto;
}
.search-page {
  .search-header {
    padding-bottom: 2.5rem;
    padding-top: 1.25rem;
  }

  .result-panel {
    margin-top: 20px;
    background-color: rgb(244 244 245);
    border-radius: 0.375em;
  }

  .filter-sidebar {
    border-right: 1px solid #dbdbdb;
    padding-top: 1.5rem;
  }

  .filter-section {
    padding: 1.5rem 0;
    border-bottom: 1px solid #dbdbdb;

    &:first-child {
      padding-top: 0;
    }
  }

  .collapse-trigger {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    padding: 0.5rem 0;

    .title {
      margin-bottom: 0;
    }
  }

  .price-inputs {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .field.has-addons {
    margin-bottom: 0px;
  }

  .price-inputs {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .price-slider {
    padding: 0 1rem;
  }

  .option-label {
    display: flex;
    align-items: center;
    font-size: 0.875rem;
  }

  .borderBox {
    background-color: white;
    display: block;
    padding: 1.25rem;
    border: 10px solid #e6e6e6;
    border-radius: 8px;
  }

  .button.narrow {
    padding-left: 0.5em;
    padding-right: 0.5em;
  }

  // Mobile optimizations
  @media screen and (max-width: 1023px) {
    .filter-sidebar {
      display: none;
    }

    .mobile-filters {
      .filter-section {
        padding: 1rem 0;
      }
    }

    .borderBox {
      padding: 0.5rem;
      border: 0;
      border-radius: 0;
    }
  }
}
</style>
